import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Loading, Badge, gbp, fmtDate } from "@/components/ui-bits";
import {
  Workflow as WorkflowIcon, Plus, X, Loader2, Lock, CheckCircle2, Circle, ChevronRight,
  ArrowLeft, Trash2, FileText, Paperclip, Play, ListChecks, PoundSterling, LayoutTemplate,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Lettings", "Refurbishment", "Compliance", "Sales", "General"];
const CAT_COLOR = {
  Lettings: "bg-blue-100 text-blue-700", Refurbishment: "bg-amber-100 text-amber-700",
  Compliance: "bg-red-100 text-red-700", Sales: "bg-emerald-100 text-emerald-700", General: "bg-slate-100 text-slate-600",
};

export default function Workflows() {
  const [tab, setTab] = useState("workflows");
  const [searchParams] = useSearchParams();
  useEffect(() => { if (searchParams.get("tab") === "templates") setTab("templates"); }, [searchParams]);
  return (
    <Layout title="Workflows & SOPs" subtitle="Process Engine">
      <div className="flex gap-2 mb-6">
        <TabBtn active={tab === "workflows"} onClick={() => setTab("workflows")} testid="tab-workflows" icon={WorkflowIcon}>Active Workflows</TabBtn>
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testid="tab-templates" icon={LayoutTemplate}>Templates</TabBtn>
      </div>
      {tab === "workflows" ? <WorkflowsTab /> : <TemplatesTab />}
    </Layout>
  );
}

const TabBtn = ({ active, onClick, children, testid, icon: Icon }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
    <Icon size={16} /> {children}
  </button>
);

/* ----------------------------- Workflows tab ----------------------------- */
function WorkflowsTab() {
  const [wfs, setWfs] = useState(null);
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/workflows").then(({ data }) => setWfs(data));
  useEffect(() => { load(); }, []);

  const openWf = async (id) => { const { data } = await api.get(`/workflows/${id}`); setSel(data); };

  if (!wfs) return <Loading />;
  if (sel) return <WorkflowDetail wf={sel} onBack={() => { setSel(null); load(); }} onChange={setSel} />;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button data-testid="start-workflow-btn" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2"><Play size={15} /> Start workflow</button>
      </div>
      {wfs.length === 0 ? (
        <Card className="p-10 text-center text-slate-400 text-sm">No workflows yet. Start one from a template or blank.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {wfs.map((wf) => {
            const done = wf.stages.filter((s) => s.status === "Complete").length;
            return (
              <Card key={wf.workflow_id} data-testid={`workflow-card-${wf.workflow_id}`} onClick={() => openWf(wf.workflow_id)}
                className="p-5 cursor-pointer hover:shadow-md transition-shadow fade-up">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CAT_COLOR[wf.category] || CAT_COLOR.General}`}>{wf.category}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${wf.status === "Completed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{wf.status}</span>
                </div>
                <p className="font-semibold text-slate-900">{wf.name}</p>
                {wf.linked_ref && <p className="text-xs text-blue-700 font-mono mt-0.5">{wf.linked_ref}</p>}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-slate-500 mb-1"><span>{done}/{wf.stages.length} stages</span><span>{wf.stages.length ? Math.round((done / wf.stages.length) * 100) : 0}%</span></div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-slate-900 rounded-full transition-all" style={{ width: `${wf.stages.length ? (done / wf.stages.length) * 100 : 0}%` }} /></div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {open && <StartWorkflowModal onClose={() => setOpen(false)} onCreated={(wf) => { setOpen(false); setSel(wf); load(); }} />}
    </div>
  );
}

function StartWorkflowModal({ onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [props, setProps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ template_id: "", name: "", linked_type: "", linked_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/workflow-templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/properties").then(({ data }) => setProps(data)).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { template_id: f.template_id || null, name: f.name, linked_type: f.linked_type || null, linked_id: f.linked_id };
      const { data } = await api.post("/workflows", body);
      toast.success("Workflow started");
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start workflow"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Start a workflow" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Sel label="From template" testid="wf-template" value={f.template_id} onChange={(v) => setF({ ...f, template_id: v })}>
          <option value="">— Blank workflow —</option>
          {templates.map((t) => <option key={t.workflow_template_id} value={t.workflow_template_id}>{t.name} ({t.category})</option>)}
        </Sel>
        {!f.template_id && <Inp label="Workflow name" testid="wf-name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required placeholder="e.g. New Tenancy Onboarding" />}
        <Sel label="Link to (optional)" testid="wf-link-type" value={f.linked_type} onChange={(v) => setF({ ...f, linked_type: v, linked_id: "" })}>
          <option value="">— Standalone —</option>
          <option value="property">Property</option>
          <option value="project">Project</option>
        </Sel>
        {f.linked_type === "property" && (
          <Sel label="Property" testid="wf-link-prop" value={f.linked_id} onChange={(v) => setF({ ...f, linked_id: v })}>
            <option value="">Select…</option>
            {props.map((p) => <option key={p.property_id} value={p.property_id}>{p.reference} — {p.address}</option>)}
          </Sel>
        )}
        {f.linked_type === "project" && (
          <Sel label="Project" testid="wf-link-proj" value={f.linked_id} onChange={(v) => setF({ ...f, linked_id: v })}>
            <option value="">Select…</option>
            {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </Sel>
        )}
        <button data-testid="wf-create-submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Start workflow
        </button>
      </form>
    </Modal>
  );
}

function WorkflowDetail({ wf, onBack, onChange }) {
  const done = wf.stages.filter((s) => s.status === "Complete").length;

  const act = async (url) => {
    try { const { data } = await api.post(url); onChange(data.workflow); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const removeWf = async () => {
    if (!window.confirm(`Delete workflow "${wf.name}"?`)) return;
    await api.delete(`/workflows/${wf.workflow_id}`);
    toast.success("Workflow deleted");
    onBack();
  };

  return (
    <div>
      <button onClick={onBack} data-testid="wf-back" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={15} /> All workflows</button>
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CAT_COLOR[wf.category] || CAT_COLOR.General}`}>{wf.category}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${wf.status === "Completed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{wf.status}</span>
          </div>
          <h2 className="text-2xl font-display font-semibold text-slate-900">{wf.name}</h2>
          {wf.linked_ref && <p className="text-sm text-blue-700 font-mono">{wf.linked_ref}</p>}
          <p className="text-xs text-slate-400 mt-1">{done}/{wf.stages.length} stages complete</p>
        </div>
        <button onClick={removeWf} data-testid="wf-delete" className="border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5"><Trash2 size={14} /> Delete</button>
      </div>
      <div className="space-y-4">
        {wf.stages.map((s, i) => <StageCard key={s.key} wf={wf} stage={s} index={i} onAct={act} />)}
      </div>
    </div>
  );
}

function StageCard({ wf, stage, index, onAct }) {
  const locked = stage.status === "Locked";
  const complete = stage.status === "Complete";
  const base = `/workflows/${wf.workflow_id}/stages/${stage.key}`;
  const StatusIcon = complete ? CheckCircle2 : locked ? Lock : Circle;
  const iconColor = complete ? "text-emerald-600" : locked ? "text-slate-400" : "text-blue-600";

  return (
    <Card data-testid={`stage-${stage.key}`} className={`p-5 ${locked ? "opacity-70" : ""} ${complete ? "border-emerald-200 bg-emerald-50/30" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 shrink-0">{index + 1}</div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 flex items-center gap-2">{stage.name} <StatusIcon size={15} className={iconColor} /></p>
            <p className="text-xs text-slate-400">{stage.status}{locked && stage.depends_on.length ? " — complete prior stage(s) first" : ""}</p>
            {stage.assignee && (
              <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                <ListChecks size={11} className="text-blue-600" /> {stage.assignee}
                {stage.task_id && !locked && <span className="text-blue-600">· on their task list</span>}
              </p>
            )}
          </div>
        </div>
      </div>

      {stage.sop_text && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5"><FileText size={12} /> SOP — how to do this</p>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{stage.sop_text}</p>
        </div>
      )}

      {stage.checklist.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {stage.checklist.map((it) => (
            <button key={it.item_id} data-testid={`check-${it.item_id}`} disabled={locked || complete}
              onClick={() => onAct(`${base}/checklist/${it.item_id}/toggle`)}
              className="flex items-center gap-2 text-left w-full disabled:cursor-not-allowed">
              {it.done ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> : <Circle size={16} className="text-slate-300 shrink-0" />}
              <span className={`text-sm ${it.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{it.text}</span>
            </button>
          ))}
        </div>
      )}

      {!locked && <StageAttachments stageKey={stage.key} />}

      {!locked && (
        <div className="mt-4 flex gap-2">
          {complete ? (
            <button data-testid={`reopen-${stage.key}`} onClick={() => onAct(`${base}/reopen`)} className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-xs font-medium">Reopen stage</button>
          ) : (
            <button data-testid={`complete-${stage.key}`} onClick={() => onAct(`${base}/complete`)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5"><CheckCircle2 size={14} /> Mark stage complete</button>
          )}
        </div>
      )}
    </Card>
  );
}

function StageAttachments({ stageKey }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/files?entity_type=workflow_stage&entity_id=${stageKey}&bucket=documents`).then(({ data }) => setFiles(data)).catch(() => {});
  useEffect(() => { load(); }, [stageKey]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("entity_type", "workflow_stage");
    fd.append("entity_id", stageKey);
    fd.append("bucket", "documents");
    fd.append("file", file);
    try { await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Attached"); load(); }
    catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-2">
        {files.map((f) => (
          <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`stage-file-${f.file_id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:border-blue-300">
            <Paperclip size={12} /> {f.original_filename}
          </a>
        ))}
        <label className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-500 hover:bg-slate-50 cursor-pointer" data-testid={`stage-upload-${stageKey}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} SOP file
          <input type="file" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>
    </div>
  );
}

/* ----------------------------- Templates tab ----------------------------- */
function TemplatesTab() {
  return (
    <div className="space-y-8">
      <WorkflowTemplates />
      <BudgetTemplates />
      <ChecklistTemplates />
    </div>
  );
}

function SectionHead({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-slate-700" />
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function WorkflowTemplates() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/workflow-templates").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this template?")) return; await api.delete(`/workflow-templates/${id}`); load(); };

  return (
    <section>
      <SectionHead icon={WorkflowIcon} title="Workflow templates" subtitle="Reusable stage-by-stage processes with SOPs & dependencies"
        action={<button data-testid="new-wf-template" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-3 py-1.5 text-sm font-semibold flex items-center gap-1.5"><Plus size={14} /> New template</button>} />
      {list.length === 0 ? <Card className="p-6 text-center text-slate-400 text-sm">No workflow templates yet.</Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((t) => (
            <Card key={t.workflow_template_id} data-testid={`wf-template-${t.workflow_template_id}`} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CAT_COLOR[t.category] || CAT_COLOR.General}`}>{t.category}</span>
                </div>
                <button onClick={() => del(t.workflow_template_id)} data-testid={`del-wf-template-${t.workflow_template_id}`} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
              <p className="text-xs text-slate-500 mt-2">{t.stages.length} stage{t.stages.length !== 1 ? "s" : ""}</p>
            </Card>
          ))}
        </div>
      )}
      {open && <WorkflowTemplateBuilder onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </section>
  );
}

function WorkflowTemplateBuilder({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", category: "General", description: "" });
  const [stages, setStages] = useState([{ key: `s${Date.now()}`, name: "", sop_text: "", assignee: "", checklist: [""], depends_on: [] }]);
  const [busy, setBusy] = useState(false);

  const setStage = (i, patch) => setStages((s) => s.map((st, idx) => idx === i ? { ...st, ...patch } : st));
  const addStage = () => setStages((s) => [...s, { key: `s${Date.now()}${s.length}`, name: "", sop_text: "", assignee: "", checklist: [""], depends_on: [] }]);
  const rmStage = (i) => setStages((s) => s.filter((_, idx) => idx !== i));
  const toggleDep = (i, key) => setStage(i, { depends_on: stages[i].depends_on.includes(key) ? stages[i].depends_on.filter((k) => k !== key) : [...stages[i].depends_on, key] });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        stages: stages.map((st, i) => ({ key: st.key, name: st.name, order: i, sop_text: st.sop_text, assignee: st.assignee,
          depends_on: st.depends_on, checklist: st.checklist.map((c) => c.trim()).filter(Boolean) })),
      };
      await api.post("/workflow-templates", payload);
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="New workflow template" onClose={onClose} wide>
      <form onSubmit={save} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Template name" testid="wft-name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required placeholder="e.g. New Tenancy Onboarding" />
          <Sel label="Category" testid="wft-category" value={f.category} onChange={(v) => setF({ ...f, category: v })}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </Sel>
        </div>
        <Inp label="Description" testid="wft-desc" value={f.description} onChange={(v) => setF({ ...f, description: v })} placeholder="Optional" />

        <div className="space-y-3">
          {stages.map((st, i) => (
            <div key={st.key} className="rounded-xl border border-slate-200 p-3 bg-slate-50/50" data-testid={`wft-stage-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500">Stage {i + 1}</span>
                {stages.length > 1 && <button type="button" onClick={() => rmStage(i)} className="text-slate-300 hover:text-red-500"><X size={15} /></button>}
              </div>
              <Inp label="Stage name" testid={`wft-stage-name-${i}`} value={st.name} onChange={(v) => setStage(i, { name: v })} required placeholder="e.g. Referencing" />
              <div className="mt-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Assign to (auto-creates a task on their to-do list)</label>
                <input data-testid={`wft-stage-assignee-${i}`} value={st.assignee} onChange={(e) => setStage(i, { assignee: e.target.value })} placeholder="e.g. Sarah Mills" className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div className="mt-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">SOP — standard operating procedure</label>
                <textarea data-testid={`wft-stage-sop-${i}`} rows={2} value={st.sop_text} onChange={(e) => setStage(i, { sop_text: e.target.value })} placeholder="Written instructions for this stage…" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div className="mt-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Checklist items</label>
                {st.checklist.map((c, ci) => (
                  <div key={ci} className="flex gap-2 mb-1.5">
                    <input data-testid={`wft-check-${i}-${ci}`} value={c} onChange={(e) => setStage(i, { checklist: st.checklist.map((x, xi) => xi === ci ? e.target.value : x) })} placeholder="e.g. Verify ID documents" className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    {st.checklist.length > 1 && <button type="button" onClick={() => setStage(i, { checklist: st.checklist.filter((_, xi) => xi !== ci) })} className="text-slate-300 hover:text-red-500"><X size={14} /></button>}
                  </div>
                ))}
                <button type="button" onClick={() => setStage(i, { checklist: [...st.checklist, ""] })} className="text-xs text-blue-700 hover:underline">+ Add item</button>
              </div>
              {i > 0 && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Depends on (locks until complete)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {stages.slice(0, i).map((prev) => (
                      <button key={prev.key} type="button" onClick={() => toggleDep(i, prev.key)}
                        className={`text-xs rounded-full px-2.5 py-1 border ${st.depends_on.includes(prev.key) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
                        {prev.name || `Stage ${stages.indexOf(prev) + 1}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <button type="button" onClick={addStage} data-testid="wft-add-stage" className="w-full border border-dashed border-slate-300 rounded-lg py-2 text-sm text-slate-500 hover:bg-slate-50 flex items-center justify-center gap-1.5"><Plus size={14} /> Add stage</button>
        </div>

        <button data-testid="wft-save" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ListChecks size={16} />} Save template
        </button>
      </form>
    </Modal>
  );
}

function BudgetTemplates() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [apply, setApply] = useState(null);
  const load = () => api.get("/budget-templates").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this budget template?")) return; await api.delete(`/budget-templates/${id}`); load(); };

  return (
    <section>
      <SectionHead icon={PoundSterling} title="Budget templates" subtitle="Pre-filled cost breakdowns you can apply to a project"
        action={<button data-testid="new-budget-template" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-3 py-1.5 text-sm font-semibold flex items-center gap-1.5"><Plus size={14} /> New template</button>} />
      {list.length === 0 ? <Card className="p-6 text-center text-slate-400 text-sm">No budget templates yet.</Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((t) => {
            const total = t.lines.reduce((s, l) => s + (l.amount || 0), 0);
            return (
              <Card key={t.budget_template_id} data-testid={`budget-template-${t.budget_template_id}`} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <button onClick={() => del(t.budget_template_id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
                </div>
                <p className="text-xs text-slate-500 mt-1">{t.lines.length} lines · {gbp(total)}</p>
                <button data-testid={`apply-budget-${t.budget_template_id}`} onClick={() => setApply(t)} className="mt-3 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-xs font-medium">Apply to project</button>
              </Card>
            );
          })}
        </div>
      )}
      {open && <BudgetBuilder onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {apply && <ApplyBudgetModal tpl={apply} onClose={() => setApply(null)} />}
    </section>
  );
}

function BudgetBuilder({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [lines, setLines] = useState([{ label: "", amount: "" }]);
  const [busy, setBusy] = useState(false);
  const total = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/budget-templates", { name, lines: lines.filter((l) => l.label.trim()).map((l) => ({ label: l.label, amount: parseFloat(l.amount) || 0 })) });
      toast.success("Budget template saved"); onSaved();
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  return (
    <Modal title="New budget template" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <Inp label="Template name" testid="bgt-name" value={name} onChange={setName} required placeholder="e.g. Standard Kitchen Refurb" />
        <div className="space-y-1.5">
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2">
              <input data-testid={`bgt-line-label-${i}`} value={l.label} onChange={(e) => setLines(lines.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))} placeholder="Line item" className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <input data-testid={`bgt-line-amount-${i}`} type="number" value={l.amount} onChange={(e) => setLines(lines.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))} placeholder="£" className="w-28 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              {lines.length > 1 && <button type="button" onClick={() => setLines(lines.filter((_, xi) => xi !== i))} className="text-slate-300 hover:text-red-500"><X size={16} /></button>}
            </div>
          ))}
          <button type="button" onClick={() => setLines([...lines, { label: "", amount: "" }])} className="text-xs text-blue-700 hover:underline">+ Add line</button>
        </div>
        <p className="text-sm font-semibold text-slate-700 text-right">Total: {gbp(total)}</p>
        <button data-testid="bgt-save" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-60">Save budget template</button>
      </form>
    </Modal>
  );
}

function ApplyBudgetModal({ tpl, onClose }) {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!pid) return;
    setBusy(true);
    try { const { data } = await api.post(`/budget-templates/${tpl.budget_template_id}/apply`, { project_id: pid }); toast.success(`Applied — project budget is now ${gbp(data.budget)}`); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Apply "${tpl.name}"`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Sel label="Project" testid="apply-budget-project" value={pid} onChange={setPid}>
          <option value="">Select a project…</option>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
        </Sel>
        <button data-testid="apply-budget-submit" disabled={busy || !pid} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-60">Add budget lines to project</button>
      </form>
    </Modal>
  );
}

function ChecklistTemplates() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [apply, setApply] = useState(null);
  const load = () => api.get("/checklist-templates").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this checklist template?")) return; await api.delete(`/checklist-templates/${id}`); load(); };
  return (
    <section>
      <SectionHead icon={ListChecks} title="Checklist templates" subtitle="Apply to a property or project — spins up a tracked checklist"
        action={<button data-testid="new-checklist-template" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-3 py-1.5 text-sm font-semibold flex items-center gap-1.5"><Plus size={14} /> New template</button>} />
      {list.length === 0 ? <Card className="p-6 text-center text-slate-400 text-sm">No checklist templates yet.</Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((t) => (
            <Card key={t.checklist_template_id} data-testid={`checklist-template-${t.checklist_template_id}`} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-slate-900">{t.name}</p>
                <button onClick={() => del(t.checklist_template_id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
              <p className="text-xs text-slate-500 mt-1">{t.items.length} items</p>
              <button data-testid={`apply-checklist-${t.checklist_template_id}`} onClick={() => setApply(t)} className="mt-3 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-xs font-medium">Apply</button>
            </Card>
          ))}
        </div>
      )}
      {open && <ChecklistBuilder onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {apply && <ApplyChecklistModal tpl={apply} onClose={() => setApply(null)} />}
    </section>
  );
}

function ChecklistBuilder({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [items, setItems] = useState([""]);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/checklist-templates", { name, items: items.map((i) => i.trim()).filter(Boolean) }); toast.success("Checklist template saved"); onSaved(); }
    catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  return (
    <Modal title="New checklist template" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <Inp label="Template name" testid="clt-name" value={name} onChange={setName} required placeholder="e.g. Move-in Inspection" />
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <input data-testid={`clt-item-${i}`} value={it} onChange={(e) => setItems(items.map((x, xi) => xi === i ? e.target.value : x))} placeholder="Checklist item" className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              {items.length > 1 && <button type="button" onClick={() => setItems(items.filter((_, xi) => xi !== i))} className="text-slate-300 hover:text-red-500"><X size={16} /></button>}
            </div>
          ))}
          <button type="button" onClick={() => setItems([...items, ""])} className="text-xs text-blue-700 hover:underline">+ Add item</button>
        </div>
        <button data-testid="clt-save" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-60">Save checklist template</button>
      </form>
    </Modal>
  );
}

function ApplyChecklistModal({ tpl, onClose }) {
  const [props, setProps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ linked_type: "", linked_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/properties").then(({ data }) => setProps(data)).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/checklist-templates/${tpl.checklist_template_id}/apply`, { linked_type: f.linked_type || null, linked_id: f.linked_id }); toast.success("Checklist created under Workflows"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Apply "${tpl.name}"`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Sel label="Link to (optional)" testid="apply-cl-type" value={f.linked_type} onChange={(v) => setF({ linked_type: v, linked_id: "" })}>
          <option value="">— Standalone —</option>
          <option value="property">Property</option>
          <option value="project">Project</option>
        </Sel>
        {f.linked_type === "property" && (
          <Sel label="Property" testid="apply-cl-prop" value={f.linked_id} onChange={(v) => setF({ ...f, linked_id: v })}>
            <option value="">Select…</option>
            {props.map((p) => <option key={p.property_id} value={p.property_id}>{p.reference} — {p.address}</option>)}
          </Sel>
        )}
        {f.linked_type === "project" && (
          <Sel label="Project" testid="apply-cl-proj" value={f.linked_id} onChange={(v) => setF({ ...f, linked_id: v })}>
            <option value="">Select…</option>
            {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </Sel>
        )}
        <button data-testid="apply-cl-submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-60">Create checklist</button>
      </form>
    </Modal>
  );
}

/* ----------------------------- shared bits ----------------------------- */
function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const Inp = ({ label, value, onChange, testid, required, placeholder, type = "text" }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
  </div>
);

const Sel = ({ label, value, onChange, testid, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
      {children}
    </select>
  </div>
);
